/**
 * Locale registry — dependency-free so proxy.ts (edge) and server/client code
 * share one source of truth.
 *
 * ONE RULE, TWO HALVES (docs/i18n-plan.md):
 *   marketing pages -> the URL decides the language
 *   everything else -> the person's preference decides
 *
 * English is the default and canonical at the unprefixed path; every other
 * locale lives under its own prefix (/sv, /de). `/en/<path>` is the explicit
 * English address for readers who want one document in English without
 * changing their preference.
 *
 * ADDING A LOCALE is deliberately a compile error, not a runtime surprise:
 * the corpus TRANSLATIONS maps (lib/use-cases, lib/blog) are keyed by
 * `TranslatedLocale`, so the build fails until each has a content.<locale>.ts.
 * That is how you find the work rather than shipping English prose under a
 * foreign URL.
 */

export const LOCALES = ["en", "sv", "de"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/** Locales that appear as a URL path prefix (everything except the default). */
export const PREFIXED_LOCALES: readonly Locale[] = LOCALES.filter((l) => l !== DEFAULT_LOCALE);

/**
 * Locales that need their own translation of in-repo content — everything
 * except the source language. Shared by the corpus modules so the `"en"`
 * literal and the reason for it live in one place.
 *
 * Not `Exclude<Locale, typeof DEFAULT_LOCALE>`: `DEFAULT_LOCALE` is typed as
 * `Locale`, so that collapses to `never` and would silently accept an empty
 * translation map.
 */
export type TranslatedLocale = Exclude<Locale, "en">;

/**
 * Each locale's name IN ITS OWN LANGUAGE, and deliberately never translated.
 *
 * That is the whole job of a language picker: someone stuck in a language they
 * cannot read has to be able to find their own. "German" helps nobody who only
 * reads German. Both pickers — the public header menu and the profile menu —
 * render from this, so the two cannot drift; they previously kept identical
 * copies in separate files.
 *
 * `Record<Locale, …>` means a new locale is a compile error here too.
 */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  sv: "Svenska",
  de: "Deutsch",
};

/**
 * A flag per locale, for the menu items in both pickers.
 *
 * DECORATION, NEVER THE IDENTIFIER. A flag is a country and a locale is a
 * language, and the two genuinely disagree: German is also Austria's and much
 * of Switzerland's, Swedish is also spoken in Finland, and English belongs to
 * no one flag at all (🇬🇧 is the convention in EU-facing products; 🇺🇸 would be
 * just as arbitrary). So the flag only ever appears NEXT TO
 * {@link LOCALE_NAMES}, which is what actually tells a reader which entry is
 * theirs — it must never become the only thing distinguishing two options.
 *
 * `aria-hidden` wherever it renders: a screen reader announcing "flag of the
 * United Kingdom" adds nothing over the adjacent "English", and for a locale
 * whose flag is a compromise it actively misleads.
 */
export const LOCALE_FLAGS: Record<Locale, string> = {
  en: "🇬🇧",
  sv: "🇸🇪",
  de: "🇩🇪",
};

/**
 * Request header carrying the RENDER locale the proxy resolved, read by
 * `getLocale()`.
 *
 * Why a header and not the cookie it used to reuse: on marketing pages the
 * language is a property of the URL, and on app pages it is a property of the
 * person. Carrying the marketing answer in `sq_lang` meant reading `/sv/terms`
 * silently rewrote the preference that `/chat` renders from — and, the other
 * way round, made `/terms` serve Swedish to anyone who had ever visited a /sv
 * page. One URL, two documents. The header keeps the per-request answer per
 * request.
 */
export const LOCALE_HEADER = "x-sq-locale";

/**
 * Split a leading locale prefix off a pathname — the ONE place that knows what
 * a locale prefix looks like. Everything that strips one calls this.
 *
 * `/sv/terms` -> { locale: "sv", path: "/terms" }; `/en` -> { "en", "/" };
 * `/terms` -> { locale: null, path: "/terms" }.
 *
 * It matches EVERY locale, including the default: `/en/<path>` is a real
 * address meaning "show me this in English without changing my language" (the
 * "English version prevails" link on a translated legal page). Matching only
 * the non-default prefixes is the bug this function exists to prevent — a
 * leftover copy that did so read `/en/terms` as a page called "en" and built
 * `/sv/en/terms`, a 404.
 */
export const splitLocalePath = (
  pathname: string,
): { locale: Locale | null; path: string } => {
  const hit = LOCALES.find(
    (l) => pathname === `/${l}` || pathname.startsWith(`/${l}/`),
  );
  return hit
    ? { locale: hit, path: pathname.slice(hit.length + 1) || "/" }
    : { locale: null, path: pathname };
};

/**
 * The inverse of {@link splitLocalePath}: a path under `locale`.
 *
 * The root special-case (`/sv`, never `/sv/`) is here once. It used to be
 * spelled four different ways across `localizeHref`, `svPath`, `switchLocale`
 * and the proxy redirect — and `/sv/` is a different URL from `/sv` to a
 * crawler, on the canonical tag.
 */
export const withLocalePrefix = (path: string, locale: Locale): string =>
  path === "/" ? `/${locale}` : `/${locale}${path}`;

/**
 * The person's standing language PREFERENCE — what app, auth and admin pages
 * render in, and the fallback for anything the proxy did not resolve.
 *
 * It no longer tracks the page being viewed. It used to be set from the /sv
 * path prefix on every visit, which quietly conflated two different things:
 * reading one Swedish marketing page rewrote the language of the agent chat,
 * and having once read one made /terms serve Swedish forever. Marketing pages
 * now carry their language in the URL ({@link LOCALE_HEADER}); this cookie is
 * written on first-visit negotiation and by the language switcher, and
 * otherwise left alone.
 */
export const LOCALE_COOKIE = "sq_lang";

/**
 * Which locale the person actually ASKED for, set only by the language
 * switcher. Deliberately separate from {@link LOCALE_COOKIE}.
 *
 * The render cookie is negotiated from the browser's `Accept-Language` header
 * on a first visit, and login used to copy it into `users.ui_language` — which
 * the runtime reads to pick the language of emails and notices. So a browser
 * configured for Swedish was enough to commit an account to Swedish mail
 * forever, without anyone choosing anything. A header is something the browser
 * sends; a choice is something a person makes, and only the second is worth
 * persisting.
 *
 * Being strict here does NOT mean the header stops mattering — be precise
 * about what it buys. With nothing stored, `resolveUserLanguage` falls back
 * to the user's own agent's `instances.language`, and for a freshly hired
 * agent that was itself detected from Accept-Language at hire
 * (`discoveryLanguageHint`). So the first guess can land in the same place.
 *
 * What changes is whether the guess is CORRECTABLE. `instances.language` is
 * documented as detected, and the agent's `set_language` tool moves it when
 * the conversation shows otherwise. `users.ui_language` is a stated
 * preference: it outranks everything and nothing ever revises it. Recording a
 * header as a preference is what made a wrong guess permanent.
 */
export const LOCALE_CHOICE_COOKIE = "sq_lang_choice";

export const isLocale = (value: string | undefined | null): value is Locale =>
  LOCALES.includes(value as Locale);

/** A cookie jar, as both `next/headers`' store and a test fake satisfy it. */
export interface CookieReader {
  get(name: string): { value: string } | undefined;
}

/**
 * The locale a person explicitly picked, or null if they never did.
 *
 * The one rule worth having in a single place: read
 * {@link LOCALE_CHOICE_COOKIE}, never {@link LOCALE_COOKIE}. Reading the
 * render cookie here is the bug this module exists to prevent — it always has
 * a value, so "did they choose?" would always answer yes.
 */
export const chosenLocale = (cookies: CookieReader): Locale | null => {
  const value = cookies.get(LOCALE_CHOICE_COOKIE)?.value;
  return isLocale(value) ? value : null;
};

/** Best-match against an Accept-Language header; DEFAULT_LOCALE when nothing matches. */
export const negotiateLocale = (acceptLanguage: string | null): Locale => {
  if (!acceptLanguage) return DEFAULT_LOCALE;
  const wanted = acceptLanguage
    .split(",")
    .map((part) => part.split(";")[0]?.trim().toLowerCase().split("-")[0])
    .filter((tag): tag is string => Boolean(tag));
  return (wanted.find((tag) => isLocale(tag)) as Locale | undefined) ?? DEFAULT_LOCALE;
};

/**
 * What the render preference (`sq_lang`) should become for this request.
 *
 * THREE INPUTS, THREE DIFFERENT WEIGHTS — the rule the locale system turns on,
 * so it lives here as pure policy rather than inline in proxy.ts:
 *
 * 1. A NON-DEFAULT locale prefix wins. Navigating to /de/pricing says which
 *    language you want, and the app half (chat, onboarding, settings) is
 *    unprefixed by design — so without this it keeps rendering whatever the
 *    browser header negotiated. That is the bug this exists to prevent: a
 *    Swedish-configured browser reading the German site got a German site and
 *    a SWEDISH onboarding agent, because the opener's language hint reads
 *    this preference.
 * 2. `/en/<path>` does NOT win. It is the "show me this ONE document in
 *    English" escape hatch — the English-prevails link on a translated legal
 *    page — and its whole contract is that it changes nothing else. Nor does
 *    an unprefixed path: those are the canonical URLs every external link
 *    points at, so honouring them would wipe a German reader's preference
 *    every time they followed one.
 * 3. Otherwise keep what is stored, negotiating from the header only when
 *    nothing is stored at all.
 *
 * Note what this is NOT: a choice. Browsing changes what you SEE; only the
 * switcher writes {@link LOCALE_CHOICE_COOKIE}, and only that is persisted to
 * `users.ui_language` or allowed to trigger the marketing redirect.
 */
export const nextPreference = (
  pathLocale: Locale | null,
  storedLocale: string | undefined,
  acceptLanguage: string | null,
): Locale =>
  pathLocale !== null && pathLocale !== DEFAULT_LOCALE
    ? pathLocale
    : isLocale(storedLocale)
      ? storedLocale
      : negotiateLocale(acceptLanguage);

/** Public marketing paths that exist under the /sv prefix. App, auth, admin,
 *  and API routes are deliberately NOT prefixable — their language comes from
 *  the cookie alone. */
export const MARKETING_PATHS: readonly string[] = [
  "/",
  "/pricing",
  "/integrations",
  "/about",
  "/business",
  "/blog",
  // Customer testimonials and every story under it (`/customers/<slug>`).
  "/customers",
  // Segment pages. The prefix covers every `/for/<profession>` without another
  // edit here — matching is prefix-aware (see `urlDecidesLocale`).
  "/for",
  "/get-started",
  "/account-inactive",
  "/privacy",
  "/terms",
  "/subprocessors",
  "/squidler-app",
];

/**
 * Does the URL decide this page's language?
 *
 * True for marketing pathnames (locale prefix already stripped), where the
 * address IS the document. False for app, auth, admin and API routes, which
 * are unprefixed by design and render in the person's preferred language.
 *
 * Deliberately not called `isMarketingPath`: lib/marketing-routes.ts already
 * exports one of those, answering a different question (may session replay
 * record this page?). Two same-named predicates with different answers is how
 * someone ends up importing the wrong one.
 */
export const urlDecidesLocale = (pathname: string): boolean => matchesPathPrefix(MARKETING_PATHS, pathname);

/** Is `pathname` one of `paths` or under one (`/blog` covers `/blog/a-post`;
 *  `/` covers only itself)? The one prefix rule for every marketing path list. */
export const matchesPathPrefix = (paths: readonly string[], pathname: string): boolean =>
  paths.some((p) => pathname === p || (p !== "/" && pathname.startsWith(`${p}/`)));

/** Same rule for an href, which may carry a query string. */
const isMarketingHref = (href: string): boolean =>
  urlDecidesLocale(href.split("?")[0] ?? href);

/** Prefix an internal marketing href with the locale (non-default locales
 *  only), so navigation from /sv stays inside /sv. Non-marketing and external
 *  hrefs pass through untouched. */
export const localizeHref = (href: string, locale: Locale): string => {
  if (locale === DEFAULT_LOCALE || !href.startsWith("/") || !isMarketingHref(href)) return href;
  return withLocalePrefix(href, locale);
};
